import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { 
  Table, 
  TableBody, 
  TableCell, 
  TableHead, 
  TableHeader, 
  TableRow 
} from '@/components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { 
  Search, 
  Plus, 
  Trash2, 
  Tag, 
  Calendar,
  Edit2,
  Percent,
  DollarSign
} from 'lucide-react';
import { useLanguage } from '@/contexts/LanguageContext';
import { 
  collection, 
  getDocs, 
  doc, 
  deleteDoc, 
  addDoc, 
  updateDoc,
  query, 
  orderBy 
} from 'firebase/firestore';
import { db } from '@/lib/firebase/config';
import { toast } from 'sonner';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';

interface Offer {
  id: string;
  title: string;
  description: string;
  discountType: 'percentage' | 'fixed';
  discountValue: number;
  originalPrice?: number;
  finalPrice?: number;
  code: string;
  usageLimit: number;
  usedCount: number;
  validFrom: Date;
  validUntil: Date;
  createdAt: Date;
  isActive: boolean;
  category: string;
}

export function AdminOfferManagement() {
  const { t, dir } = useLanguage();
  const [offers, setOffers] = useState<Offer[]>([]);
  const [filteredOffers, setFilteredOffers] = useState<Offer[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [offerToDelete, setOfferToDelete] = useState<Offer | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editingOffer, setEditingOffer] = useState<Offer | null>(null);
  const [creating, setCreating] = useState(false);
  const [updating, setUpdating] = useState(false);
  const [newOffer, setNewOffer] = useState({
    title: '',
    description: '',
    discountType: 'percentage' as 'percentage' | 'fixed',
    discountValue: 0,
    originalPrice: 0,
    code: '',
    usageLimit: 0,
    validFrom: '',
    validUntil: '',
    category: 'general',
    isActive: true
  });

  const fetchOffers = async () => {
    try {
      setLoading(true);
      
      const offersRef = collection(db, 'offers');
      const offersQuery = query(offersRef, orderBy('createdAt', 'desc'));
      const offersSnapshot = await getDocs(offersQuery);

      const offersData = offersSnapshot.docs.map(doc => {
        const data = doc.data();
        return {
          id: doc.id,
          ...data,
          createdAt: data.createdAt?.toDate() || new Date(),
          validFrom: data.validFrom?.toDate() || new Date(),
          validUntil: data.validUntil?.toDate() || new Date(),
          usedCount: data.usedCount || 0
        } as Offer;
      });

      setOffers(offersData);
      setFilteredOffers(offersData);
    } catch (error) {
      console.error('Error fetching offers:', error);
      toast.error(t('dashboard.admin.offers.messages.loadError'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchOffers();
  }, []);

  useEffect(() => {
    if (!searchQuery.trim()) {
      setFilteredOffers(offers);
    } else {
      const filtered = offers.filter(offer =>
        offer.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
        offer.description.toLowerCase().includes(searchQuery.toLowerCase()) ||
        offer.code.toLowerCase().includes(searchQuery.toLowerCase()) ||
        offer.category.toLowerCase().includes(searchQuery.toLowerCase())
      );
      setFilteredOffers(filtered);
    }
  }, [searchQuery, offers]);

  const handleDeleteClick = (offer: Offer) => {
    setOfferToDelete(offer);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!offerToDelete) return;

    try {
      setDeleting(true);
      await deleteDoc(doc(db, 'offers', offerToDelete.id));
      
      setOffers(prev => prev.filter(o => o.id !== offerToDelete.id));
      toast.success(t('dashboard.admin.offers.messages.deleteSuccess'));
    } catch (error) {
      console.error('Error deleting offer:', error);
      toast.error(t('dashboard.admin.offers.messages.deleteError'));
    } finally {
      setDeleting(false);
      setDeleteDialogOpen(false);
      setOfferToDelete(null);
    }
  };

  const calculateFinalPrice = () => {
    if (newOffer.discountType === 'percentage') {
      return newOffer.originalPrice - (newOffer.originalPrice * newOffer.discountValue / 100);
    } else {
      return newOffer.originalPrice - newOffer.discountValue;
    }
  };

  const handleCreateOffer = async () => {
    if (!newOffer.title.trim() || !newOffer.code.trim()) {
      toast.error('Title and code are required');
      return;
    }

    try {
      setCreating(true);
      const offerData = {
        ...newOffer,
        createdAt: new Date(),
        validFrom: new Date(newOffer.validFrom),
        validUntil: new Date(newOffer.validUntil),
        finalPrice: calculateFinalPrice(),
        usedCount: 0
      };

      const docRef = await addDoc(collection(db, 'offers'), offerData);
      
      const newOfferWithId = {
        id: docRef.id,
        ...offerData
      };

      setOffers(prev => [newOfferWithId, ...prev]);
      setNewOffer({
        title: '',
        description: '',
        discountType: 'percentage',
        discountValue: 0,
        originalPrice: 0,
        code: '',
        usageLimit: 0,
        validFrom: '',
        validUntil: '',
        category: 'general',
        isActive: true
      });
      setCreateDialogOpen(false);
      toast.success(t('dashboard.admin.offers.messages.createSuccess'));
    } catch (error) {
      console.error('Error creating offer:', error);
      toast.error(t('dashboard.admin.offers.messages.createError'));
    } finally {
      setCreating(false);
    }
  };

  const handleEditClick = (offer: Offer) => {
    setEditingOffer(offer);
    setNewOffer({
      title: offer.title,
      description: offer.description,
      discountType: offer.discountType,
      discountValue: offer.discountValue,
      originalPrice: offer.originalPrice || 0,
      code: offer.code,
      usageLimit: offer.usageLimit,
      validFrom: offer.validFrom.toISOString().split('T')[0],
      validUntil: offer.validUntil.toISOString().split('T')[0],
      category: offer.category,
      isActive: offer.isActive
    });
    setEditDialogOpen(true);
  };

  const handleUpdateOffer = async () => {
    if (!editingOffer || !newOffer.title.trim() || !newOffer.code.trim()) {
      toast.error('Title and code are required');
      return;
    }

    try {
      setUpdating(true);
      const updateData = {
        title: newOffer.title,
        description: newOffer.description,
        discountType: newOffer.discountType,
        discountValue: newOffer.discountValue,
        originalPrice: newOffer.originalPrice,
        code: newOffer.code,
        usageLimit: newOffer.usageLimit,
        validFrom: new Date(newOffer.validFrom),
        validUntil: new Date(newOffer.validUntil),
        category: newOffer.category,
        isActive: newOffer.isActive,
        finalPrice: calculateFinalPrice()
      };

      await updateDoc(doc(db, 'offers', editingOffer.id), updateData);

      setOffers(prev => prev.map(o => 
        o.id === editingOffer.id 
          ? { 
              ...o, 
              ...updateData,
              validFrom: new Date(newOffer.validFrom),
              validUntil: new Date(newOffer.validUntil)
            }
          : o
      ));

      setEditDialogOpen(false);
      setEditingOffer(null);
      setNewOffer({
        title: '',
        description: '',
        discountType: 'percentage',
        discountValue: 0,
        originalPrice: 0,
        code: '',
        usageLimit: 0,
        validFrom: '',
        validUntil: '',
        category: 'general',
        isActive: true
      });
      toast.success(t('dashboard.admin.offers.messages.updateSuccess'));
    } catch (error) {
      console.error('Error updating offer:', error);
      toast.error(t('dashboard.admin.offers.messages.updateError'));
    } finally {
      setUpdating(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-muted-foreground">{t('dashboard.admin.offers.loading')}</div>
      </div>
    );
  }

  return (
    <div className="space-y-6" dir={dir}>
      <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
        <div className="space-y-1">
          <h2 className="text-2xl font-bold tracking-tight">
            {t('dashboard.admin.offers.title')}
          </h2>
          <p className="text-muted-foreground">
            {t('dashboard.admin.offers.pageDescription')}
          </p>
        </div>
        <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
          <DialogTrigger asChild>
            <Button>
              <Plus className={`${dir === 'rtl' ? 'ml-2' : 'mr-2'} h-4 w-4`} />
              {t('dashboard.admin.offers.createOffer')}
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle>{t('dashboard.admin.offers.createOffer')}</DialogTitle>
              <DialogDescription>
                {t('dashboard.admin.offers.createOfferDescription')}
              </DialogDescription>
            </DialogHeader>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="title">{t('dashboard.admin.offers.offerTitle')}</Label>
                <Input
                  id="title"
                  value={newOffer.title}
                  onChange={(e) => setNewOffer(prev => ({ ...prev, title: e.target.value }))}
                  placeholder={t('dashboard.admin.offers.titlePlaceholder')}
                />
              </div>
              <div>
                <Label htmlFor="code">{t('dashboard.admin.offers.code')}</Label>
                <Input
                  id="code"
                  value={newOffer.code}
                  onChange={(e) => setNewOffer(prev => ({ ...prev, code: e.target.value.toUpperCase() }))}
                  placeholder={t('dashboard.admin.offers.codePlaceholder')}
                />
              </div>
              <div className="col-span-2">
                <Label htmlFor="description">{t('dashboard.admin.offers.description')}</Label>
                <Textarea
                  id="description"
                  value={newOffer.description}
                  onChange={(e) => setNewOffer(prev => ({ ...prev, description: e.target.value }))}
                  placeholder={t('dashboard.admin.offers.descriptionPlaceholder')}
                />
              </div>
              <div>
                <Label htmlFor="discountType">{t('dashboard.admin.offers.discountType')}</Label>
                <Select value={newOffer.discountType} onValueChange={(value: 'percentage' | 'fixed') => setNewOffer(prev => ({ ...prev, discountType: value }))}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="percentage">{t('dashboard.admin.offers.percentage')}</SelectItem>
                    <SelectItem value="fixed">{t('dashboard.admin.offers.fixedAmount')}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label htmlFor="discountValue">{t('dashboard.admin.offers.discountValue')}</Label>
                <Input
                  id="discountValue"
                  type="number"
                  value={newOffer.discountValue}
                  onChange={(e) => setNewOffer(prev => ({ ...prev, discountValue: Number(e.target.value) }))}
                  placeholder="0"
                />
              </div>
              <div>
                <Label htmlFor="originalPrice">{t('dashboard.admin.offers.originalPrice')}</Label>
                <Input
                  id="originalPrice"
                  type="number"
                  value={newOffer.originalPrice}
                  onChange={(e) => setNewOffer(prev => ({ ...prev, originalPrice: Number(e.target.value) }))}
                  placeholder="0"
                />
              </div>
              <div>
                <Label htmlFor="usageLimit">{t('dashboard.admin.offers.usageLimit')}</Label>
                <Input
                  id="usageLimit"
                  type="number"
                  value={newOffer.usageLimit}
                  onChange={(e) => setNewOffer(prev => ({ ...prev, usageLimit: Number(e.target.value) }))}
                  placeholder="0"
                />
              </div>
              <div>
                <Label htmlFor="category">{t('dashboard.admin.offers.category')}</Label>
                <Select value={newOffer.category} onValueChange={(value) => setNewOffer(prev => ({ ...prev, category: value }))}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="general">{t('dashboard.admin.offers.categories.general')}</SelectItem>
                    <SelectItem value="courses">{t('dashboard.admin.offers.categories.courses')}</SelectItem>
                    <SelectItem value="trips">{t('dashboard.admin.offers.categories.trips')}</SelectItem>
                    <SelectItem value="services">{t('dashboard.admin.offers.categories.services')}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label htmlFor="validFrom">{t('dashboard.admin.offers.validFrom')}</Label>
                <Input
                  id="validFrom"
                  type="date"
                  value={newOffer.validFrom}
                  onChange={(e) => setNewOffer(prev => ({ ...prev, validFrom: e.target.value }))}
                />
              </div>
              <div>
                <Label htmlFor="validUntil">{t('dashboard.admin.offers.validUntil')}</Label>
                <Input
                  id="validUntil"
                  type="date"
                  value={newOffer.validUntil}
                  onChange={(e) => setNewOffer(prev => ({ ...prev, validUntil: e.target.value }))}
                />
              </div>
              {newOffer.originalPrice > 0 && (
                <div className="col-span-2">
                  <Label>{t('dashboard.admin.offers.finalPrice')}</Label>
                  <div className="text-lg font-semibold text-green-600">
                    ${calculateFinalPrice().toFixed(2)}
                  </div>
                </div>
              )}
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setCreateDialogOpen(false)}>
                {t('dashboard.admin.offers.cancel')}
              </Button>
              <Button onClick={handleCreateOffer} disabled={creating}>
                {creating ? t('dashboard.admin.offers.creating') : t('dashboard.admin.offers.create')}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      {/* Search */}
      <div className="flex items-center gap-4">
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground h-4 w-4" />
          <Input
            placeholder={t('dashboard.admin.offers.searchPlaceholder')}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10"
          />
        </div>
        <div className="text-sm text-muted-foreground">
          {filteredOffers.length} {t('dashboard.admin.offers.of')} {offers.length} {t('dashboard.admin.offers.offersCount')}
        </div>
      </div>

      {/* Offers Table */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Tag className="h-5 w-5" />
            {t('dashboard.admin.offers.allOffers')}
          </CardTitle>
        </CardHeader>
        <CardContent>
          {filteredOffers.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              {searchQuery ? t('dashboard.admin.offers.noOffersFound') : t('dashboard.admin.offers.noOffers')}
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>{t('dashboard.admin.offers.tableHeaders.offer')}</TableHead>
                    <TableHead>{t('dashboard.admin.offers.tableHeaders.code')}</TableHead>
                    <TableHead>{t('dashboard.admin.offers.tableHeaders.discount')}</TableHead>
                    <TableHead>{t('dashboard.admin.offers.tableHeaders.usage')}</TableHead>
                    <TableHead>{t('dashboard.admin.offers.tableHeaders.validity')}</TableHead>
                    <TableHead>{t('dashboard.admin.offers.tableHeaders.status')}</TableHead>
                    <TableHead className="text-right">{t('dashboard.admin.offers.tableHeaders.actions')}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredOffers.map((offer) => (
                    <TableRow key={offer.id}>
                      <TableCell>
                        <div>
                          <div className="font-medium">{offer.title}</div>
                          <div className="text-sm text-muted-foreground line-clamp-2">
                            {offer.description}
                          </div>
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline" className="font-mono">
                          {offer.code}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-1">
                          {offer.discountType === 'percentage' ? (
                            <>
                              <Percent className="h-4 w-4 text-muted-foreground" />
                              {offer.discountValue}%
                            </>
                          ) : (
                            <>
                              <DollarSign className="h-4 w-4 text-muted-foreground" />
                              {offer.discountValue}
                            </>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="text-sm">
                          {offer.usedCount}/{offer.usageLimit}
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="text-sm">
                          <div>{offer.validFrom.toLocaleDateString()}</div>
                          <div className="text-muted-foreground">to {offer.validUntil.toLocaleDateString()}</div>
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge variant={offer.isActive ? "default" : "secondary"}>
                          {offer.isActive ? t('dashboard.admin.offers.status.active') : t('dashboard.admin.offers.status.inactive')}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex items-center justify-end gap-2">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleEditClick(offer)}
                            className="text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                          >
                            <Edit2 className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleDeleteClick(offer)}
                            className="text-red-600 hover:text-red-700 hover:bg-red-50"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Edit Offer Dialog */}
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>{t('dashboard.admin.offers.editOffer')}</DialogTitle>
            <DialogDescription>
              {t('dashboard.admin.offers.editOfferDescription')}
            </DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label htmlFor="edit-title">{t('dashboard.admin.offers.offerTitle')}</Label>
              <Input
                id="edit-title"
                value={newOffer.title}
                onChange={(e) => setNewOffer(prev => ({ ...prev, title: e.target.value }))}
                placeholder={t('dashboard.admin.offers.titlePlaceholder')}
              />
            </div>
            <div>
              <Label htmlFor="edit-code">{t('dashboard.admin.offers.code')}</Label>
              <Input
                id="edit-code"
                value={newOffer.code}
                onChange={(e) => setNewOffer(prev => ({ ...prev, code: e.target.value.toUpperCase() }))}
                placeholder={t('dashboard.admin.offers.codePlaceholder')}
              />
            </div>
            <div className="col-span-2">
              <Label htmlFor="edit-description">{t('dashboard.admin.offers.description')}</Label>
              <Textarea
                id="edit-description"
                value={newOffer.description}
                onChange={(e) => setNewOffer(prev => ({ ...prev, description: e.target.value }))}
                placeholder={t('dashboard.admin.offers.descriptionPlaceholder')}
              />
            </div>
            <div>
              <Label htmlFor="edit-discountType">{t('dashboard.admin.offers.discountType')}</Label>
              <Select value={newOffer.discountType} onValueChange={(value: 'percentage' | 'fixed') => setNewOffer(prev => ({ ...prev, discountType: value }))}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="percentage">{t('dashboard.admin.offers.percentage')}</SelectItem>
                  <SelectItem value="fixed">{t('dashboard.admin.offers.fixedAmount')}</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label htmlFor="edit-discountValue">{t('dashboard.admin.offers.discountValue')}</Label>
              <Input
                id="edit-discountValue"
                type="number"
                value={newOffer.discountValue}
                onChange={(e) => setNewOffer(prev => ({ ...prev, discountValue: Number(e.target.value) }))}
                placeholder="0"
              />
            </div>
            <div>
              <Label htmlFor="edit-originalPrice">{t('dashboard.admin.offers.originalPrice')}</Label>
              <Input
                id="edit-originalPrice"
                type="number"
                value={newOffer.originalPrice}
                onChange={(e) => setNewOffer(prev => ({ ...prev, originalPrice: Number(e.target.value) }))}
                placeholder="0"
              />
            </div>
            <div>
              <Label htmlFor="edit-usageLimit">{t('dashboard.admin.offers.usageLimit')}</Label>
              <Input
                id="edit-usageLimit"
                type="number"
                value={newOffer.usageLimit}
                onChange={(e) => setNewOffer(prev => ({ ...prev, usageLimit: Number(e.target.value) }))}
                placeholder="0"
              />
            </div>
            <div>
              <Label htmlFor="edit-category">{t('dashboard.admin.offers.category')}</Label>
              <Select value={newOffer.category} onValueChange={(value) => setNewOffer(prev => ({ ...prev, category: value }))}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="general">{t('dashboard.admin.offers.categories.general')}</SelectItem>
                  <SelectItem value="courses">{t('dashboard.admin.offers.categories.courses')}</SelectItem>
                  <SelectItem value="trips">{t('dashboard.admin.offers.categories.trips')}</SelectItem>
                  <SelectItem value="services">{t('dashboard.admin.offers.categories.services')}</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label htmlFor="edit-validFrom">{t('dashboard.admin.offers.validFrom')}</Label>
              <Input
                id="edit-validFrom"
                type="date"
                value={newOffer.validFrom}
                onChange={(e) => setNewOffer(prev => ({ ...prev, validFrom: e.target.value }))}
              />
            </div>
            <div>
              <Label htmlFor="edit-validUntil">{t('dashboard.admin.offers.validUntil')}</Label>
              <Input
                id="edit-validUntil"
                type="date"
                value={newOffer.validUntil}
                onChange={(e) => setNewOffer(prev => ({ ...prev, validUntil: e.target.value }))}
              />
            </div>
            {newOffer.originalPrice > 0 && (
              <div className="col-span-2">
                <Label>{t('dashboard.admin.offers.finalPrice')}</Label>
                <div className="text-lg font-semibold text-green-600">
                  ${calculateFinalPrice().toFixed(2)}
                </div>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditDialogOpen(false)}>
              {t('dashboard.admin.offers.cancel')}
            </Button>
            <Button onClick={handleUpdateOffer} disabled={updating}>
              {updating ? t('dashboard.admin.offers.updating') : t('dashboard.admin.offers.update')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('dashboard.admin.offers.deleteDialog.title')}</AlertDialogTitle>
            <AlertDialogDescription>
              {t('dashboard.admin.offers.deleteDialog.description', { title: offerToDelete?.title })}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('dashboard.admin.offers.deleteDialog.cancel')}</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteConfirm}
              disabled={deleting}
              className="bg-red-600 hover:bg-red-700"
            >
              {deleting ? t('dashboard.admin.offers.deleteDialog.deleting') : t('dashboard.admin.offers.deleteDialog.confirm')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
